import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useChangeUserPaymentCardMutation } from "@features/userDetails/userDetailsApi.ts";
import { useEffect } from "react";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { ChangeUserPaymentCardParameters } from "@features/userDetails/types.ts";

export default function ChangePaymentCardSuccess() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mutation, { isLoading, isSuccess, isError }] =
    useChangeUserPaymentCardMutation();

  const status = searchParams.get("status");
  useEffect(() => {
    const data: ChangeUserPaymentCardParameters = {
      "checkout-account": searchParams.get("checkout-account") ?? "",
      "checkout-status": searchParams.get("checkout-status") ?? "",
      "checkout-tokenization-id":
        searchParams.get("checkout-tokenization-id") ?? "",
      "checkout-algorithm": searchParams.get("checkout-algorithm") ?? "",
      "checkout-method": searchParams.get("checkout-method") ?? "",
      signature: searchParams.get("signature") ?? "",
    };

    const hasRequiredSearchParams =
      Object.values(data).filter((value) => value).length ===
      Object.keys(data).length;

    if (!status && hasRequiredSearchParams) {
      void mutation(data);
    } else if (status !== "success") {
      setSearchParams({ status: "error" });
    }
  }, [mutation, searchParams, setSearchParams, status]);

  useEffect(() => {
    if (isSuccess) {
      setSearchParams({ status: "success" });
    } else if (isError) {
      setSearchParams({ status: "error" });
    }
  }, [isSuccess, isError, setSearchParams]);

  return (
    <Stack spacing={3} padding={2}>
      <Stack spacing={2}>
        <Typography variant="h4">
          {t("paymentCardChangeSuccess.title")}
        </Typography>
        {status === "success" && (
          <Alert severity="info">{t("paymentCardChangeSuccess.success")}</Alert>
        )}
        {status === "error" && (
          <Alert icon={<ErrorOutline fontSize="inherit" />} severity="error">
            {t("paymentCardChangeSuccess.error")}
          </Alert>
        )}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
